import React, { createContext, useContext } from "react";
import { useQuery } from "@apollo/client";
import { GET_AGENCY } from "../apis/queries/getAgency";

const AgencyPermissionValidationContext = createContext<
  | {
      canAccessParticipantWebapp: boolean;
      loadingAgency: boolean;
    }
  | undefined
>(undefined);

/**
 * Provider for the AgencyPermissionValidationContext.
 * Fetches agency data and computes two flags:
 * - `canAccessParticipantWebapp`: Defined by agency, defaults to `true` if unavailable.
 * - `loadingAgency`: Indicates if the agency is loading.
 */
const AgencyPermissionValidationContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: agencyData, loading: loadingAgency } = useQuery(GET_AGENCY);

  const canAccessParticipantWebapp =
    agencyData?.getAgency?.canAccessParticipantWebapp ?? true;

  return (
    <AgencyPermissionValidationContext.Provider
      value={{ canAccessParticipantWebapp, loadingAgency }}
    >
      {children}
    </AgencyPermissionValidationContext.Provider>
  );
};

export function useAgencyPermissionValidation() {
  const context = useContext(AgencyPermissionValidationContext);
  if (context === undefined) {
    throw new Error(
      "useAgencyPermissionValidation must be used within a AgencyPermissionValidationContextProvider",
    );
  }
  return context;
}

export default AgencyPermissionValidationContextProvider;
